'use client'

/**
 * FGL Coaching-App — Einführung & Anleitung
 *
 * Umfassende Doku aller Screens, Funktionen und Sprachbefehle der
 * Android-Coaching-App (aktuell v3.10.22). Nutzt AwardLegalShell für
 * konsistentes Editorial-Layout mit FAQ/Wettbewerbsordnung/Datenschutz.
 *
 * Stand: 2026-08-23 (v3.10.22)
 */

import Link from 'next/link'
import {
  AwardLegalShell,
  type LegalArticle,
} from '@/components/award/legal-shell'

// ═══════════════════════════════════════════════════════════════
// Artikel-Liste: Alle Themen der Coaching-App
// ═══════════════════════════════════════════════════════════════

const ARTICLES: LegalArticle[] = [
  {
    num: '§ 1',
    numDisplay: '01',
    title: 'Was ist die FGL Coaching-App?',
    body: (
      <>
        <p>
          Die FGL Coaching-App ist ein Android-Cockpit-Assistent für RC-Modellsegelflug.
          Sie liest Live-Telemetrie deines Senders per Bluetooth, führt dich per Sprache
          durch Start, Streckenflug, Thermik und Endanflug, und lädt deinen IGC-Flug
          nach der Landung direkt hier auf die FGL-Plattform hoch.
        </p>
        <p>
          Die App ist konsequent für den Einsatz <strong>am Segler-Standplatz</strong>
          entwickelt: Große Zahlen, hoher Kontrast, blinde Bedienbarkeit über
          Sprachbefehle, große Touch-Ziele. Alles was du unterwegs brauchst,
          ohne dass du die Augen vom Modell nehmen musst.
        </p>
        <p>
          Aktuelle Version: <strong>v3.10.22</strong> (2026-08-23) — mit
          QR-Code-Kopplung, automatischem Direct-Upload, Retry-Queue und
          Flugspur-Vorschauen.
        </p>
      </>
    ),
  },
  {
    num: '§ 2',
    numDisplay: '02',
    title: 'Installation & Berechtigungen',
    body: (
      <>
        <p>
          Die APK wird nicht über Google Play verteilt (bewusste Entscheidung — kein
          Play-Store-Store, keine Play-Approval nötig, direkte Kontrolle über
          Auslieferung). Du erhältst die aktuelle APK als Download-Link von uns.
        </p>
        <p>
          <strong>APK installieren:</strong> beim ersten Öffnen bittet Android
          um Bestätigung für „Installation aus unbekannter Quelle". Diese
          einmalig zulassen für den Datei-Manager oder Browser aus dem du die
          APK öffnest.
        </p>
        <p>
          <strong>Berechtigungen die die App braucht:</strong>
        </p>
        <ul>
          <li><strong>Bluetooth</strong> — Verbindung zum PowerBox CORE oder Jeti-Sender-Modul</li>
          <li><strong>Standort</strong> — GPS-Fixes für IGC-Aufzeichnung</li>
          <li><strong>Mikrofon</strong> — Sprachbefehle (Vosk offline oder Google online)</li>
          <li><strong>Speicher</strong> — IGC-Dateien in <code>Downloads/FGL-IGC/</code></li>
        </ul>
        <p>
          Keine Berechtigung wird über den nötigen Umfang hinaus verwendet.
          Insbesondere: die App macht <strong>keine Kamera-Zugriffe</strong> —
          der QR-Code-Kopplungs-Flow läuft über die Standard-Android-Kamera.
        </p>
      </>
    ),
  },
  {
    num: '§ 3',
    numDisplay: '03',
    title: 'Kopplung mit deinem FGL-Konto',
    body: (
      <>
        <p>
          Die Kopplung verbindet die App mit deinem FGL-Konto. Danach kann sie
          IGCs direkt hochladen und deine Modelle aus dem Hangar abrufen.
        </p>
        <p>
          <strong>Weg 1 — QR-Code (empfohlen, ab App-Version 3.10.22):</strong>
        </p>
        <ol>
          <li>Öffne im Browser deines Handys{' '}
            <Link href="/app/settings/devices" style={{ color: 'var(--accent)' }}>
              Einstellungen &rarr; Meine Geräte
            </Link>
          </li>
          <li>Klicke „Neuen Code erzeugen" — ein 6-stelliger Code + QR-Bild erscheinen</li>
          <li>Halte die Standard-Kamera-App deines Handys über den QR-Code</li>
          <li>Die Kamera erkennt den <code>fgl://pair?code=…</code>-Link und öffnet die Coaching-App automatisch</li>
          <li>Die App füllt den Code selbstständig ein — nur noch „Koppeln" klicken</li>
        </ol>
        <p>
          <strong>Weg 2 — 6-Digit-Code manuell:</strong>
        </p>
        <ol>
          <li>Gleiche Einstellungsseite auf einem beliebigen Gerät (Laptop, Tablet)</li>
          <li>Öffne den Uploads-Tab in der Coaching-App</li>
          <li>Tippe den Code in das Feld ein</li>
          <li>„Koppeln" klicken — bei Erfolg zeigt die Karte deinen Namen + Email</li>
        </ol>
        <p>
          Der Code ist <strong>10 Minuten</strong> gültig. Der Auth-Token gilt danach
          <strong> 6 Monate</strong>. Die App warnt dich <strong>ab 7 Tage vor Ablauf</strong>{' '}
          in Gold, ab 2 Tagen in Rot. Bei Ablauf wird automatisch auf den Pairing-Screen zurückgeschaltet.
        </p>
      </>
    ),
  },
  {
    num: '§ 4',
    numDisplay: '04',
    title: 'Die 17 Screens im Überblick',
    body: (
      <>
        <p>
          Die App organisiert sich als Bottom-Navigation mit 17 Tabs. Von links
          nach rechts:
        </p>
        <ul>
          <li><strong>0 — Flug (FlyScreen):</strong> Cockpit-Hauptansicht mit Riesen-Karte, künstlichem Horizont, Vario, Höhe, Sollfahrt, Wind, allen Live-NavBoxes, MC-Round-Instrument. Enthält den goldenen „▶ FGL-Start jetzt"-Button für den manuellen Start-Trigger und die Vollflächen-Celebration beim Übergang in die Zeitwertung.</li>
          <li><strong>1 — Telemetrie:</strong> Alle Rohwerte des Senders (Vario m/s, Höhe, GPS-Fix, ENL, Satelliten). Debug-Blick auf die BLE-Verbindung.</li>
          <li><strong>2 — Verbinden (Connect):</strong> Sender-Kopplung. PowerBox CORE per BLE oder Jeti-Modul, Auto-Detect, Reconnect-Timer.</li>
          <li><strong>3 — Coaching:</strong> Live-Textausgabe der KI-Coach-Ansagen. Zeigt was die Sprache gerade sagt und warum.</li>
          <li><strong>4 — Wettbewerb (Competition):</strong> Zeitwertung (30 min / 1 h / 2 h / 3 h / +3 h), Klassen-Zuordnung, FGL-Regel-Status.</li>
          <li><strong>5 — Vario-Einstellungen:</strong> TEK-Damping, Netto-Mischanteil, Vario-Ton-Profil (10 Presets: LX, Cambridge, Borgelt, Peschges…).</li>
          <li><strong>6 — Endanflug (Final-Glide-Settings):</strong> Sicherheitshöhe, MC-Wert, Way-to-Home-Intervall, alle Slider für den Endanflug-Rechner.</li>
          <li><strong>7 — Sensor-Setup:</strong> Wizard zur Sensor-Zuordnung wenn du einen neuen Sender koppelst.</li>
          <li><strong>8 — Thermik-Zentrieren:</strong> Bird&#39;s-Eye-View mit weighted-centroid Kern-Estimation (XCSoar-Style), Live-Coach-Ansagen „Einleiten links — jetzt!" bei Bart-Entry, „Aufrichten — jetzt!" bei Verlust.</li>
          <li><strong>9 — Einstellungen:</strong> Theme, TTS-Setup, Login-Flow, App-Info, Geräte-Wechsel.</li>
          <li><strong>10 — Uploads:</strong> Pairing-Karte, Aircraft-Picker mit Suche, alle IGCs aus 5 Scan-Roots mit Flugspur-Thumbnail, Live-Upload mit Progress-Bar, Retry-Queue-Card wenn Fehler wartet.</li>
          <li><strong>11 — Vosk-Diagnose:</strong> Wake-Word-Test, Grammar-Debug, welcher Text kommt gerade an.</li>
          <li><strong>12 — Sprachbefehle:</strong> Referenzliste aller ~76 Kommandos nach Kategorien sortiert. Deine Nachschlage-Seite wenn du unsicher bist.</li>
          <li><strong>13 — Streckenflug:</strong> Sollfahrt-Riesenzahl (nach MacCready), MC-Chip, MC-Ton-Toggle, LiveTrackMapCanvas mit Pilot mittig und farbigem Schweif nach Vario.</li>
          <li><strong>14 — Polare:</strong> Polarkurve des aktuellen Modells (a·v² + b·v + c), Gold-Punkt = Best-L/D, Cyan-Punkt = MC-Sollfahrt-Optimum, grüner Live-Punkt = aktueller Betriebspunkt.</li>
          <li><strong>15 — Wetter:</strong> Auto-Fetch beim ersten GPS-Fix. Wind, Böen, QNH, Wolkenbasis, Thermik-Index für dein aktuelles Fluggebiet.</li>
          <li><strong>16 — Thermik-Atlas:</strong> Karte mit Gold-Kreisen (Atlas-bekannte Bärte) und Cyan-Kreisen (live-erkannt). Radius-Toggle 5/20/50 km. JSON-Import aus <code>Downloads/thermal-atlas-*.json</code>.</li>
        </ul>
      </>
    ),
  },
  {
    num: '§ 5',
    numDisplay: '05',
    title: 'FlyScreen — deine Cockpit-Ansicht',
    body: (
      <>
        <p>
          Der FlyScreen ist der wichtigste Screen. Er läuft die ganze Zeit während
          du fliegst und zeigt alles was für Sekunden-Entscheidungen zählt.
        </p>
        <p>
          <strong>Kern-Anzeigen:</strong> Riesen-Karte (Bird&#39;s-Eye oder Nord-Up
          umschaltbar), 3D-Segler-Modell mit korrektem Heading, Höhen-AGL über
          Startpunkt, Vario-Ton + Zahl, Live-Wind-Vektor, Kompass, künstlicher
          Horizont (einblendbar).
        </p>
        <p>
          <strong>NavBoxes:</strong> Die Info-Kacheln unten/oben können per Long-Press
          angepasst werden — 30+ Werte zur Auswahl (Groundspeed, Airspeed, ENL,
          Battery, MacCready-Sollfahrt, Distanz zum Home, GLZ, Netto-Vario, TEK,
          uvm.).
        </p>
        <p>
          <strong>Der goldene FGL-Start-Button:</strong> mittig unten auf der Karte.
          Wenn du losfliegst und der Auto-Detektor dich nicht erkennt (z.B. Slope-
          Start ohne klassisches Sinken), klick ihn — er zählt 10 Sekunden per TTS
          runter, setzt den Anker und schreibt einen Referenz-Pin in die IGC.
        </p>
        <p>
          <strong>FGL-Start-Celebration (v3.10.18+):</strong> beim ersten Übergang
          in die STARTED-Phase (egal ob Auto- oder Manual-Trigger) sieht der ganze
          Screen 3,2 Sekunden lang einen Gold-Puls-Flash, „FGL-START!" als
          Riesenlabel, drei Sterne und „Zeitwertung läuft". Parallel dazu die
          TTS-Ansage <em>„Zeitwertung läuft. Viel Erfolg!"</em>. Feuert genau einmal —
          nicht permanent solange STARTED aktiv ist.
        </p>
      </>
    ),
  },
  {
    num: '§ 6',
    numDisplay: '06',
    title: 'Sprachbefehle — Grundlagen',
    body: (
      <>
        <p>
          Die Sprachbefehle sind das eigentliche Cockpit-Interface. Du sollst die App
          <strong> ohne hinsehen</strong> bedienen können, während dein Modell fliegt.
        </p>
        <p>
          <strong>Zwei Modi:</strong>
        </p>
        <ul>
          <li><strong>Online (Google SpeechRecognizer):</strong> Alle Wörter frei
              erkennbar, hohe Trefferquote, braucht Internet. Empfohlen im
              Standard-Modus.</li>
          <li><strong>Offline (Vosk):</strong> Läuft ohne Internet, hat feste
              Grammar (~65 Wörter). Wichtig für Fluggebiete ohne Mobilfunk.</li>
        </ul>
        <p>
          Umschaltung: im Einstellungen-Tab (9) oder per Sprache selber
          („<em>Online-Modus</em>" / „<em>Offline-Modus</em>").
        </p>
        <p>
          <strong>Wake-Words:</strong> die App hört immer zu, reagiert aber nur
          nach einem Wake-Word. Sag <strong>„Hallo"</strong> oder <strong>„Free Glide"</strong>{' '}
          (seit v3.10.9 beide gleichwertig), warte auf den Kling-Ton, dann sag
          dein Kommando innerhalb eines 5-Sekunden-Fensters.
        </p>
        <p>
          <strong>Der Vosk-Status-Punkt:</strong> rechts vom Mikrofon-Button auf
          dem FlyScreen. Rot pulsierend = aktiv + Wake-Word wartet. Gelb = Service
          gestartet aber Modell nicht bereit. Grau = Vosk deaktiviert.
        </p>
        <p>
          <strong>Wenn ein Kommando nicht erkannt wird</strong>, sagt die App
          <em>„Nicht erkannt. Ich hörte: [was du gesagt hast]"</em> — hilft beim
          Debuggen. Halluzinationen (falsches Erkennen von „leise" → 5 Minuten
          MUTE) sind seit v3.10.13 mit Wort-Grenz-Regex gefixt.
        </p>
      </>
    ),
  },
  {
    num: '§ 7',
    numDisplay: '07',
    title: 'Sprachbefehle — Screen-Navigation',
    body: (
      <>
        <p>
          Ab v3.10.14 reicht ein Wort. Ohne Präfix. So kommst du zu jedem Tab:
        </p>
        <ul>
          <li>„<strong>Flug</strong>" / „<strong>Haupt</strong>" / „<strong>Fly</strong>" &rarr; FlyScreen (0)</li>
          <li>„<strong>Telemetrie</strong>" &rarr; Telemetrie (1)</li>
          <li>„<strong>Koppeln</strong>" &rarr; Connect (2)</li>
          <li>„<strong>Coaching</strong>" &rarr; Coaching (3)</li>
          <li>„<strong>Wettbewerb</strong>" &rarr; Competition (4)</li>
          <li>„<strong>Vario-Einstellungen</strong>" &rarr; Vario-Settings (5)</li>
          <li>„<strong>Endanflug-Einstellungen</strong>" &rarr; Final-Glide (6)</li>
          <li>„<strong>Sensor-Setup</strong>" &rarr; Sensor-Setup (7)</li>
          <li>„<strong>Thermik</strong>" / „<strong>Bart</strong>" / „<strong>Zentrierer</strong>" &rarr; Thermik-Zentrieren (8)</li>
          <li>„<strong>Einstellungen</strong>" &rarr; Settings (9)</li>
          <li>„<strong>Hochladen</strong>" / „<strong>Uploads</strong>" / „<strong>Flugliste</strong>" &rarr; Uploads (10)</li>
          <li>„<strong>Vosk</strong>" / „<strong>Spracherkennung</strong>" &rarr; Vosk-Diagnose (11)</li>
          <li>„<strong>Befehle</strong>" / „<strong>Kommandos</strong>" / „<strong>Sprachbefehle</strong>" &rarr; Sprachbefehle-Liste (12)</li>
          <li>„<strong>Strecke</strong>" / „<strong>Streckenflug</strong>" / „<strong>Überland</strong>" &rarr; Streckenflug (13)</li>
          <li>„<strong>Polare</strong>" / „<strong>Polar</strong>" &rarr; Polare (14)</li>
          <li>„<strong>Wetter</strong>" &rarr; Wetter (15)</li>
          <li>„<strong>Atlas</strong>" / „<strong>Thermikatlas</strong>" / „<strong>Bartkarte</strong>" &rarr; Thermik-Atlas (16)</li>
        </ul>
        <p>
          Wenn du unsicher bist welche Screens du hast, sag <strong>„Befehle vorlesen"</strong> —
          die App trägt die 8 Kern-Kategorien in Ruhe vor.
        </p>
      </>
    ),
  },
  {
    num: '§ 8',
    numDisplay: '08',
    title: 'Sprachbefehle — Flug-Aktionen',
    body: (
      <>
        <p>
          <strong>Aufzeichnung:</strong>
        </p>
        <ul>
          <li>„<strong>Aufzeichnung starten</strong>" — beginnt manuelles IGC-Recording</li>
          <li>„<strong>Aufzeichnung stoppen</strong>" — beendet + veröffentlicht IGC</li>
          <li>„<strong>Start hier</strong>" — setzt Home-Anker an der aktuellen GPS-Position</li>
          <li>„<strong>FGL-Start jetzt</strong>" — manueller FGL-Start-Trigger mit 10s-Countdown</li>
        </ul>
        <p>
          <strong>MacCready + Endanflug:</strong>
        </p>
        <ul>
          <li>„<strong>MC eins komma fünf</strong>" — setzt MC-Wert 1,5 m/s (deutsche Zahlen werden geparst: eins/zwei/drei/vier/fünf/komma/null…)</li>
          <li>„<strong>MC auto</strong>" — MacCready adaptiv aus aktueller Steig-Rate</li>
          <li>„<strong>MC manuell</strong>" — MacCready fest auf zuletzt gesetzten Wert</li>
          <li>„<strong>Endanflug an</strong>" / „<strong>Endanflug aus</strong>" — Way-to-Home-Modus</li>
          <li>„<strong>Sicherheitshöhe hundert</strong>" — Reserve-Höhe für Endanflug-Rechner (in Metern)</li>
          <li>„<strong>Zeitwertung sechzig</strong>" — Ziel-Flugzeit in Minuten für die Sollfahrt-Rechnung</li>
        </ul>
        <p>
          <strong>Karten-Toggles (seit v3.10.17):</strong>
        </p>
        <ul>
          <li>„<strong>Karten-Thermik an</strong>" / „<strong>Karten-Thermik aus</strong>" — Thermik-Marker auf allen Live-Karten</li>
          <li>„<strong>Karten-Wind an</strong>" / „<strong>Karten-Wind aus</strong>" — Wind-Vektor auf allen Live-Karten</li>
          <li>„<strong>MC-Ton an</strong>" / „<strong>MC-Ton aus</strong>" — Sollfahrt-Ton im Streckenflug-Screen</li>
          <li>„<strong>Zentrier-Ansage an</strong>" / „<strong>Zentrier-Ansage aus</strong>" — Thermik-Coach-Ansagen</li>
        </ul>
        <p>
          <strong>Systemsteuerung:</strong>
        </p>
        <ul>
          <li>„<strong>Ruhe</strong>" / „<strong>Stille</strong>" / „<strong>Mute</strong>" — 30 Sekunden TTS-Pause (v3.10.13; früher 5 Min = zu lang)</li>
          <li>„<strong>Laut</strong>" — MUTE sofort aufheben</li>
          <li>„<strong>Online-Modus</strong>" / „<strong>Offline-Modus</strong>" — Spracherkennung umschalten</li>
        </ul>
        <p>
          Voll persistiert werden die Karten-Toggles seit v3.10.18 — sie überleben
          App-Neustart. Der MUTE-Status ist bewusst Session-only.
        </p>
      </>
    ),
  },
  {
    num: '§ 9',
    numDisplay: '09',
    title: 'Direct-Upload nach der Landung',
    body: (
      <>
        <p>
          Ab v3.10.16 lädt die App deinen IGC-Flug direkt nach der Landung hoch —
          kein Umweg mehr über den separaten FGL-IGC-Uploader.
        </p>
        <p>
          <strong>Voraussetzungen:</strong>
        </p>
        <ol>
          <li>App gekoppelt (siehe § 3)</li>
          <li>Modell im Aircraft-Picker gewählt (siehe § 10)</li>
          <li>Auto-Flight-Detector hat Landung erkannt (Sink → &lt;3 km/h für 30 s)</li>
        </ol>
        <p>
          <strong>Ablauf:</strong> IGC wird geschrieben, in
          <code> Downloads/FGL-IGC/</code> exportiert (bleibt IMMER dort, auch wenn
          Upload fehlschlägt), dann Multipart-POST an <code>/api/app/upload</code> mit
          <code> X-App-Token</code>-Header. Voice-Feedback bei Erfolg
          („Upload erfolgreich, Wertung 12,34 km"), Duplikat, Manipulation oder
          Netz-Fehler.
        </p>
        <p>
          <strong>Bei Netz-Fehler:</strong> ab v3.10.19 fällt der IGC nicht mehr durch
          die Ritzen. Er wird in die <strong>Retry-Queue</strong> (§ 10) mit Backoff
          gepackt und automatisch neu versucht (30 s / 2 min / 10 min / 30 min / 2 h,
          max 5 Versuche). Voice-Ansage <em>„Upload fehlgeschlagen. Wird
          automatisch wiederholt."</em>
        </p>
      </>
    ),
  },
  {
    num: '§ 10',
    numDisplay: '10',
    title: 'Uploads-Screen — Pairing, Aircraft-Picker, Retry-Queue',
    body: (
      <>
        <p>
          Der Uploads-Screen (Tab 10) ist deine zentrale Anlaufstelle für alles
          rund um Konto-Verbindung + IGC-Handling.
        </p>
        <p>
          <strong>Pairing-Karte oben:</strong> Wenn nicht gekoppelt zeigt sie ein
          6-stelliges Zifferninput + „Koppeln"-Button. QR-Deep-Link (§ 3) füllt das
          Feld automatisch. Wenn gekoppelt: Pilot-Name + Email + „Abmelden"-Button.
          Farb-Warnung ab 7 Tagen Token-Restlaufzeit, rot ab 2 Tagen. Overflow-Warnung
          wenn du versehentlich 7+ Ziffern tippst.
        </p>
        <p>
          <strong>Aircraft-Karte:</strong> zeigt dein zuletzt gewähltes Modell (bleibt
          persistent zwischen App-Starts). Antippen öffnet das Aircraft-Picker-Modal
          mit deinem Hangar. Ab 6 Modellen im Hangar erscheint ein Live-Suchfeld
          (Manufacturer / Model / Custom-Name / Klasse). Auswahl aktiviert Direct-
          Upload nach Landung.
        </p>
        <p>
          <strong>Flug-Liste:</strong> alle IGCs aus 5 Scan-Roots
          (<code>Downloads/FGL-IGC/</code>, Coaching-Recordings, drei Legacy-Pfade).
          Sortiert nach Datum. Jede Zeile mit <strong>Silhouetten-Thumbnail</strong>{' '}
          (88×66 px, DSGVO-neutral aus B-Records normalisiert), Dateiname,
          Aufnahme-Datum, Größe, Status-Badge (NEW/UPLOAD…/SCORED/ERROR/IGNORED).
        </p>
        <p>
          <strong>Retry-Queue-Card (nur wenn Items):</strong> Gold-Highlight oben.
          Zeigt Anzahl + festgefahrene Items, erste 3 mit ETA („in 45s" o.ä.),
          Buttons „Jetzt versuchen" + „Warteschlange leeren". Auto-Retry-Loop im
          Hintergrund alle 15 Sekunden solange Items da sind (v3.10.21).
        </p>
        <p>
          <strong>Success-Dialog:</strong> bei erfolgreichem Upload — km-Zahl
          animiert von 0 auf Wert (CountUp mit FastOutSlow-Easing 900 ms),
          Ø km/h mit 200 ms Verzögerung, Klasse. Dazu 60 Konfetti-Partikel als
          Gold/Cyan/Weiss-Regen, 3 Sekunden. Erscheint sowohl bei manuellem Upload
          als auch bei Auto-Retry-Success (v3.10.21 N4).
        </p>
      </>
    ),
  },
  {
    num: '§ 11',
    numDisplay: '11',
    title: 'Thermik-Zentrieren — der Coaching-Kern',
    body: (
      <>
        <p>
          Der Thermik-Zentrier-Screen (Tab 8, seit v3.10.12) ist der eigentliche
          Coaching-Kern der App. Bird&#39;s-Eye-View über 80/150/300 m Zoom, Auto-
          Skalierung, 25-m-Grid-Ringe.
        </p>
        <p>
          <strong>Weighted-centroid Kern-Estimation:</strong> die App berechnet über
          ein 60-Sekunden-Sliding-Window mit der Formel
          <code> core = Σ(pos × max(0,vario)²) / Σ(max(0,vario)²)</code> (XCSoar-
          Standard) wo der Bart-Kern liegt. Der Gold-Kern-Marker zeigt Position und
          echten Radius. Ein Puls-Ring läuft darum. Ein Bearing-Pfeil zeigt in
          welche Richtung du vom Kern weg bist.
        </p>
        <p>
          <strong>Live-Coach (weicher, empathischer Ton):</strong>
        </p>
        <ul>
          <li>„<em>Zentriert. Weiter so.</em>" — im Kern (NORMAL-Prio, 10s Cooldown)</li>
          <li>„<em>Sanft nach rechts verlagern.</em>" — nah dran (NORMAL)</li>
          <li>„<em>Bart schwächer werdend. Suche neu.</em>" — Vario im Kreis fällt</li>
          <li>„<em>Einleiten links — jetzt!</em>" — beim Bart-Entry, zeitkritisch (HIGH, kein Cooldown, 15 s Anti-Spam)</li>
          <li>„<em>Aufrichten — jetzt!</em>" — bei Bart-Exit</li>
        </ul>
        <p>
          Ton bewusst weich: „verlagern" statt „drücken", „sanft nachfassen" statt
          „nachfassen" (User-Feedback in v3.10.11).
        </p>
        <p>
          Toggle-Chip oben rechts: „🎤 Ansage An/Aus" bzw. per Sprache
          <em> „Zentrier-Ansage an/aus"</em>.
        </p>
      </>
    ),
  },
  {
    num: '§ 12',
    numDisplay: '12',
    title: 'Streckenflug + MacCready-Sound',
    body: (
      <>
        <p>
          Der Streckenflug-Screen (Tab 13, v3.10.17) hilft dir dabei, die
          optimale Fluggeschwindigkeit zwischen Thermiken zu halten.
        </p>
        <p>
          <strong>Sollfahrt-Riesenzahl:</strong> aus MacCready-Wert + aktuellem
          Sinken + Wind-Komponente berechnet die App die theoretische
          MC-Geschwindigkeit. Anzeige mit Farb-Code: Grün (im Ziel &plusmn;3 km/h),
          Gold (5–20 km/h Abweichung), Rot (deutlich weg). Diff-Zahl darunter,
          Hinweis-Text „im Ziel" / „zu schnell — sanft ziehen" / „zu langsam".
        </p>
        <p>
          <strong>MC-Sound-Generator (v3.10.17):</strong> AudioTrack mit
          kontinuierlichem Sinus-Ton der die Speed-Diff signalisiert:
        </p>
        <ul>
          <li>± 3 km/h Dead-Zone → 400 Hz Grundton</li>
          <li>+3 bis +20 zu schnell → 400 → 900 Hz linear, 2–4 Hz Puls</li>
          <li>+20+ zu schnell → 900 Hz, 8 Hz Puls</li>
          <li>-3 bis -20 zu langsam → 400 → 150 Hz, 2–4 Hz Puls</li>
          <li>Vario &gt; +0,3 m/s → <strong>Stille</strong> (Vario-Ton hat Priorität)</li>
        </ul>
        <p>
          Ein-/Aus per Toggle im Screen ODER per Voice
          „<em>MC-Ton an</em>" / „<em>MC-Ton aus</em>".
        </p>
      </>
    ),
  },
  {
    num: '§ 13',
    numDisplay: '13',
    title: 'Polare, Wetter, Thermik-Atlas',
    body: (
      <>
        <p>
          <strong>Polare-Screen (Tab 14):</strong> zeigt die Polarkurve deines
          zugewiesenen Basismodells (aus dem Hangar). Speed 30–180 km/h auf x,
          Sink 0 bis −4 m/s auf y. Gold-Punkt = Best-L/D, Cyan-Punkt = MC-
          Sollfahrt-Optimum + Tangente. Der grüne Live-Punkt wandert entlang der
          Kurve mit deinem aktuellen Betriebspunkt — sofort siehst du wo du
          eigentlich sein solltest. Karte am Rand für Kontext.
        </p>
        <p>
          <strong>Wetter-Screen (Tab 15):</strong> beim ersten GPS-Fix wird
          automatisch <code>/api/weather?lat&amp;lon</code> abgefragt. Zeigt
          Wind, Böen, QNH, Temperatur, Wolkenbasis, Thermik-Index für dein
          aktuelles Fluggebiet. Manueller „⟳ Neu laden"-Chip. Wenn Server
          offline oder Endpoint nicht produktiv: Fallback-Text.
        </p>
        <p>
          <strong>Thermik-Atlas-Screen (Tab 16):</strong> Vollbild-Karte mit
          Gold-Kreisen für Atlas-bekannte Thermiken (aus dem FGL-Server oder JSON-
          Import) und Cyan-Kreisen für live-erkannte Bärte (aus dem eigenen
          ThermalTracker, pulsieren). Radius-Toggle 5 km / 20 km / 50 km.
          „⟳ Server" holt Atlas-Daten für den aktuellen Bbox. „📂 JSON" öffnet
          einen Datei-Picker für lokale JSON-Imports aus
          <code> Downloads/thermal-atlas-*.json</code>.
        </p>
      </>
    ),
  },
  {
    num: '§ 14',
    numDisplay: '14',
    title: 'Flugspur-Thumbnails im Logbuch',
    body: (
      <>
        <p>
          Ab v3.10.20 zeigt jeder Flug-Eintrag eine winzige Vorschau seiner
          Flugspur als Gold-Kurve.
        </p>
        <p>
          <strong>Wie es funktioniert:</strong> beim ersten Öffnen des Uploads-
          oder Logbuch-Tabs parst die App die B-Records aus dem IGC (nur die
          ersten 57 Bytes pro Zeile), normalisiert die Lat/Lon-Punkte in einen
          [0..1]-Bbox-Raum, sampled auf max 300 Punkte und cached das Ergebnis
          in <code>cacheDir/silhouette/&lt;sha256&gt;.json</code>. Erster Parse:
          ~50–100 ms, danach Cache-Read &lt; 5 ms.
        </p>
        <p>
          <strong>DSGVO-neutral:</strong> der Cache enthält NUR normalisierte
          [0..1]-Punkte und Bbox-Spans in Grad. Keine Absolut-Koordinaten. Auch
          bei Cache-Diebstahl kein Standort-Rückschluss möglich.
        </p>
        <p>
          <strong>Visualisierung:</strong> Gold-Kurve auf dunklem Grund, grüner
          Start-Punkt, roter End-Punkt, Aspect-Ratio-korrekt (Lon-Skalierung mit
          cos(lat) ≈ 0,65 angenähert). Bei kaputter IGC / 0 GPS-Fixes zeigt die
          Vorschau „? kein Track".
        </p>
        <p>
          Cache wird beim App-Start auf max 500 Files getrimmt (LRU, älteste
          zuerst) — verhindert unbegrenztes Wachstum.
        </p>
      </>
    ),
  },
  {
    num: '§ 15',
    numDisplay: '15',
    title: 'Toggle-Persistenz & App-Verhalten',
    body: (
      <>
        <p>
          Ab v3.10.18 überleben deine Karten-Toggles (Thermik-Marker, Wind-Vektor)
          und der MC-Sound-Zustand den App-Neustart. Vorher waren sie session-only
          und musstest sie nach jedem Öffnen neu setzen.
        </p>
        <p>
          Speicherort: SharedPreferences <code>fgl_toggles</code> mit Keys
          <code> map_thermals</code>, <code>map_wind</code>, <code>mc_sound</code>.
          Wird bei jeder Änderung sofort persistiert (LaunchedEffect auf State).
        </p>
        <p>
          <strong>Andere persistente Einstellungen:</strong>
        </p>
        <ul>
          <li>Theme-Auswahl</li>
          <li>Gewähltes Modell (<code>PairingManager.lastUsedAircraftId</code>)</li>
          <li>Vario-Ton-Profil</li>
          <li>MC-Wert + Auto/Manual-Modus</li>
          <li>Sicherheitshöhe + Zeitwertung-Ziel</li>
          <li>Endanflug-Aktivierungs-Status</li>
          <li>Way-to-Home-Intervall</li>
        </ul>
        <p>
          <strong>Der Reset-Software-Button</strong> im FglHeader (v3.10.52) räumt
          alle SharedPreferences und Cache-Daten in einem Klick weg — praktisch
          wenn du einen fremden Sender koppeln oder Test-Reset machen willst.
          Auth-Token bleibt bewusst erhalten.
        </p>
      </>
    ),
  },
  {
    num: '§ 16',
    numDisplay: '16',
    title: 'Datenschutz & was gesendet wird',
    body: (
      <>
        <p>
          Was die App an den FGL-Server sendet:
        </p>
        <ul>
          <li><strong>Bei der Kopplung:</strong> nur den 6-Digit-Code + Device-Model + App-Version. Der Server antwortet mit einem 6-Monats-Auth-Token.</li>
          <li><strong>Bei Upload:</strong> die IGC-Datei + Aircraft-ID + optional Pilot-Name. Die IGC enthält deinen Flug-Track (Lat/Lon/Alt-Fixes). Sie wird nach den Regeln der FGL-Wertung verarbeitet.</li>
          <li><strong>Bei Wetter-/Atlas-Fetch:</strong> deine aktuelle Position (Lat/Lon), nichts sonst.</li>
        </ul>
        <p>
          Was <strong>NICHT</strong> gesendet wird:
        </p>
        <ul>
          <li>Kein Audio (Vosk ist offline, Google-Recognizer sendet nur intern an Google-STT-Server, die App leitet nichts weiter)</li>
          <li>Keine Telemetrie zwischen Uploads (keine Live-Tracking-Übertragung an FGL)</li>
          <li>Keine Analytics, kein Crash-Reporting-Provider, kein Firebase</li>
          <li>Keine Werbung, keine Ads-IDs</li>
        </ul>
        <p>
          <strong>Vault-Silhouette-Cache</strong> (Flugspur-Thumbnails) enthält nur
          normalisierte [0..1]-Punkte, keine Absolut-Koordinaten (siehe § 14).
        </p>
        <p>
          <strong>Auth-Token</strong> liegt in SharedPreferences <code>fgl_pairing_v1</code>{' '}
          (App-privater Speicher, nicht extern lesbar auf nicht-root Geräten). Bei
          401/403 vom Server wird er automatisch gelöscht.
        </p>
      </>
    ),
  },
  {
    num: '§ 17',
    numDisplay: '17',
    title: 'Support & Kontakt',
    body: (
      <>
        <p>
          Wenn etwas nicht funktioniert:
        </p>
        <ol>
          <li>Prüfe zuerst diese Seite auf den betreffenden Abschnitt</li>
          <li>Öffne den Vosk-Diagnose-Tab (11) — er zeigt live was die App hört</li>
          <li>Bug-Report per Käfer-Icon im Header (schickt Log an FGL-Team)</li>
          <li>Direkter Kontakt:{' '}
            <a href="mailto:info@modellpilot.eu" style={{ color: 'var(--accent)' }}>
              info@modellpilot.eu
            </a>
          </li>
        </ol>
        <p>
          <strong>Aktuelle Version prüfen:</strong> im Einstellungen-Tab (9)
          scroll ganz nach unten. Dort steht Version + Build-Datum. Bitte in
          Bug-Reports immer mit angeben.
        </p>
        <p>
          <strong>Neue APK-Versionen</strong> gibt es aktuell nicht über Google
          Play, sondern per Direkt-Link von uns. Die App zeigt einen Update-Hinweis
          im Header wenn eine neue Version verfügbar ist. Beim Update alte
          Version einfach überinstallieren — Kopplung und alle Einstellungen
          bleiben erhalten.
        </p>
        <p>
          Zum Registrieren, Modelle im Hangar anlegen, deine Wertung ansehen:{' '}
          <Link href="/register" style={{ color: 'var(--accent)' }}>
            free-glide-league.com/register
          </Link>
          .
        </p>
      </>
    ),
  },
]

// ═══════════════════════════════════════════════════════════════
// Page-Component
// ═══════════════════════════════════════════════════════════════

export default function CoachingAppIntroPage() {
  return (
    <AwardLegalShell
      eyebrow="Coaching-App"
      meta="Stand v3.10.22 · 2026-08-23"
      title="Deine Cockpit-{grad} für den RC-Modellsegelflug"
      gradWord="{grad}"
      lead="17 Screens, ~76 Sprachbefehle, Live-Coaching, Direct-Upload — alles was du im Cockpit brauchst. Hier ist die vollständige Anleitung."
      chips={[
        { label: 'Android' },
        { label: 'Kostenlos' },
        { label: 'Offline-fähig' },
        { label: 'DSGVO-neutral' },
      ]}
      tocEyebrow="Inhalt"
      tocTitle="Alle Themen im Überblick"
      articles={ARTICLES}
      closingEyebrow="Loslegen"
      closingTitle={
        <>
          Bereit für deinen ersten <Link href="/app/settings/devices">gekoppelten Flug</Link>?
        </>
      }
      primaryCta={{ href: '/app/settings/devices', label: 'App jetzt koppeln' }}
      secondaryCta={{ href: '/faq', label: 'FAQ ansehen' }}
      showGlider={true}
    />
  )
}
